import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InventoryValueSnapshot } from "@/core/entities/report";
import { useInventoryValueSnapshots } from "@/hooks";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});

const compactCurrencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

function formatCurrency(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "—";
  return currencyFormatter.format(value);
}

function formatNumber(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "—";
  return Math.round(value).toLocaleString();
}

function formatCpm(value: number | undefined): string {
  // Stored buy CPM is dollars per mile; show as cents per mile (¢) like Inventory.
  if (value === undefined || !Number.isFinite(value)) return "—";
  return (value * 100).toFixed(5);
}

function toDateKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

// Format a "YYYY-MM-DD" key, tolerating empty/invalid values so a single bad
// snapshot can't crash the chart axis or tooltip.
function formatDateKey(value: unknown, pattern: string): string {
  if (typeof value !== "string" || value.length === 0) return "";
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? "" : format(parsed, pattern);
}

// Earliest order activity; matches the inventory-value backfill default.
const HISTORY_START = "2024-06-01";

const RANGE_OPTIONS: { label: string; days: number | null }[] = [
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "1Y", days: 365 },
  { label: "All", days: null },
];

// Display order for the by-status breakdown; archived shown last.
const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  depleted: "Depleted",
  closed: "Closed",
  blocked: "Blocked",
  draft: "Draft",
  archived: "Archived",
};
const STATUS_ORDER = ["active", "depleted", "closed", "blocked", "draft", "archived"];

export default function InventoryValuePage() {
  const today = useMemo(() => new Date(), []);
  const todayKey = useMemo(() => toDateKey(today), [today]);

  const { data, isLoading } = useInventoryValueSnapshots({
    startDate: HISTORY_START,
    endDate: todayKey,
  });

  const snapshots = useMemo<InventoryValueSnapshot[]>(
    () => data ?? [],
    [data],
  );

  // Latest recorded snapshot (series is ascending by date).
  const latest = snapshots.length ? snapshots[snapshots.length - 1] : undefined;

  // Selected date defaults to the latest snapshot once data loads.
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const effectiveSelected = selectedDate
    ? toDateKey(selectedDate)
    : latest?.date;

  // Closest snapshot at or before the selected date (so picking a gap day still
  // shows the last known value).
  const selectedSnapshot = useMemo(() => {
    if (!effectiveSelected) return undefined;
    let match: InventoryValueSnapshot | undefined;
    for (const snapshot of snapshots) {
      if (snapshot.date <= effectiveSelected) match = snapshot;
      else break;
    }
    return match;
  }, [snapshots, effectiveSelected]);

  const [rangeDays, setRangeDays] = useState<number | null>(90);
  const chartData = useMemo(() => {
    const cutoff =
      rangeDays === null
        ? null
        : toDateKey(new Date(today.getTime() - rangeDays * 86_400_000));
    return snapshots
      .filter((s) => (cutoff ? s.date >= cutoff : true))
      .map((s) => ({
        date: s.date,
        stockValueLeft: s.stockValueLeft,
        totalCommittedCost: s.totalCommittedCost,
      }));
  }, [snapshots, rangeDays, today]);

  const splitAvailable = selectedSnapshot?.paymentSplitAvailable ?? false;

  const byStatusRows = useMemo(() => {
    const rows = selectedSnapshot?.byStatus ?? [];
    return [...rows].sort(
      (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
    );
  }, [selectedSnapshot]);

  return (
    <div className="container mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Inventory Value
          </h1>
          <p className="text-sm text-muted-foreground">
            What our stock was worth on a given date, with the supplier-payment
            split. Stock value is cost basis (miles on hand × avg buy CPM).
          </p>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-[220px] justify-start text-left font-normal",
                !effectiveSelected && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {effectiveSelected
                ? format(parseISO(effectiveSelected), "PPP")
                : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={selectedDate ?? (latest ? parseISO(latest.date) : undefined)}
              onSelect={setSelectedDate}
              disabled={(date) =>
                date > today || toDateKey(date) < HISTORY_START
              }
              autoFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {selectedSnapshot && selectedSnapshot.date !== effectiveSelected && (
        <p className="text-sm text-muted-foreground">
          No snapshot on {effectiveSelected}; showing the last recorded value
          from {selectedSnapshot.date}.
        </p>
      )}

      {/* Headline value cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Stock Value Left</CardDescription>
            <CardTitle className="text-3xl">
              {formatCurrency(selectedSnapshot?.stockValueLeft)}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Cost basis · unsold miles on hand × avg buy CPM
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Committed Cost</CardDescription>
            <CardTitle className="text-3xl">
              {formatCurrency(selectedSnapshot?.totalCommittedCost)}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            All non-voided purchases (net of buybacks)
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Miles On Hand</CardDescription>
            <CardTitle className="text-3xl">
              {formatNumber(selectedSnapshot?.milesOnHand)}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Cumulative purchased − sold as of date
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Buy CPM (¢)</CardDescription>
            <CardTitle className="text-3xl">
              {formatCpm(selectedSnapshot?.avgBuyCpm)}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Weighted average cost across all stock held
          </CardContent>
        </Card>
      </div>

      {/* Supplier payment split */}
      <Card>
        <CardHeader>
          <CardTitle>Supplier Payment Breakdown</CardTitle>
          <CardDescription>
            How the committed supplier cost splits across payment stages. These
            four buckets reconcile to Total Committed Cost.
            {!splitAvailable && (
              <>
                {" "}
                The payment split is recorded going forward (it can't be
                reconstructed for past dates), so it isn't available for this
                date.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <PaymentStatCard
              label="Paid"
              hint="Cash already cleared to suppliers"
              value={splitAvailable ? selectedSnapshot?.paidValue : undefined}
            />
            <PaymentStatCard
              label="Paid Once Booked"
              hint="Due when the item is booked/sold"
              value={
                splitAvailable
                  ? selectedSnapshot?.paidOnceBookedValue
                  : undefined
              }
            />
            <PaymentStatCard
              label="After"
              hint="Balance paid later, on terms"
              value={splitAvailable ? selectedSnapshot?.afterValue : undefined}
            />
            <PaymentStatCard
              label="Owed (Advance)"
              hint="Unpaid on advance-term purchases"
              value={
                splitAvailable ? selectedSnapshot?.advanceOwedValue : undefined
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Value over time */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>Value Over Time</CardTitle>
              <CardDescription>
                Daily stock value and total committed cost.
              </CardDescription>
            </div>
            <div className="flex gap-1">
              {RANGE_OPTIONS.map((option) => (
                <Button
                  key={option.label}
                  size="sm"
                  variant={rangeDays === option.days ? "default" : "outline"}
                  onClick={() => setRangeDays(option.days)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Loading value history…
            </p>
          ) : chartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No value snapshots yet. Run the backfill or wait for the daily
              sync.
            </p>
          ) : (
            <ChartContainer
              className="aspect-auto h-[320px] w-full"
              config={{
                stockValueLeft: {
                  label: "Stock Value Left",
                  color: "hsl(var(--primary))",
                },
                totalCommittedCost: {
                  label: "Total Committed Cost",
                  color: "hsl(var(--muted-foreground))",
                },
              }}
            >
              <LineChart data={chartData} margin={{ left: 12, right: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={32}
                  tickFormatter={(value: string) =>
                    formatDateKey(value, "MMM d")
                  }
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={64}
                  tickFormatter={(value: number) =>
                    compactCurrencyFormatter.format(value)
                  }
                />
                <Tooltip
                  formatter={(value, name) => [
                    currencyFormatter.format(Number(value)),
                    name === "stockValueLeft"
                      ? "Stock Value Left"
                      : "Total Committed Cost",
                  ]}
                  labelFormatter={(label) =>
                    formatDateKey(String(label), "PPP")
                  }
                />
                <Line
                  type="monotone"
                  dataKey="stockValueLeft"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="totalCommittedCost"
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                />
              </LineChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Closed / sold breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Stock Value Left by Status</CardTitle>
          <CardDescription>
            Where remaining (unsold) value is sitting — available miles per
            inventory status, valued at the snapshot's avg buy CPM. Closed and
            depleted accounts that still hold miles surface here, separating
            value still in stock from value already realised. This breakdown is
            recorded live, so it's only available for the current day's snapshot.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {byStatusRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No status breakdown for this date.
            </p>
          ) : (
            <Table containerClassName="overflow-auto">
              <TableHeader className="sticky top-0 z-10 bg-background">
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Miles On Hand</TableHead>
                  <TableHead className="text-right">Stock Value Left</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byStatusRows.map((row) => (
                  <TableRow
                    key={row.status}
                    className={
                      row.status === "archived"
                        ? "text-muted-foreground"
                        : undefined
                    }
                  >
                    <TableCell className="font-medium">
                      {STATUS_LABELS[row.status] ?? row.status}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(row.miles)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(row.stockValueLeft)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PaymentStatCard({
  label,
  hint,
  value,
}: {
  label: string;
  hint: string;
  value: number | undefined;
}) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{formatCurrency(value)}</p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
